import { CartApi } from '@tacans/shared-marketplace-layer';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import { useAuthorized } from 'src/features/common/auth/hooks/auth.hooks';
import { AuthModal } from 'src/features/white-labels/techcontemporary/app/components/auth';
import { selectCartItems } from 'src/features/white-labels/techcontemporary/cart/ducks/cart.selectors';
import { actions as CartActions } from 'src/features/white-labels/techcontemporary/cart/ducks/cart.slice';
import { useChooseBlockchain } from 'src/features/white-labels/techcontemporary/hooks/useChooseBlockchain';
import { useTechContemporary } from 'src/hooks/use-tech-contemporary';
import { useNotifications } from 'src/services/notifications/use-notifications';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { SvgIcon } from 'src/ui/common/atoms/svg-icon';
import { Typography } from 'src/ui/common/atoms/typography';
import { downloadPrivateFile } from 'src/utils/downloadPrivateFile';
import { getEuroFromCent } from 'src/utils/getEuroFromCent';
import {
    StyledArtType,
    StyledArtTypeTitle,
    StyledClaimButton,
    StyledContainer,
    StyledInfo,
    StyledLazyLoadImage,
    StyledSvgButton,
} from './styles';
import { BuyNowCardProps } from './types';

export const BuyNowCard: React.VFC<BuyNowCardProps> = ({ art, testId, isItMyArt, isLanding }) => {
    const { push } = useRouter();
    const { t } = useTranslation(['tech-contemporary']);
    const dispatch = useDispatch();
    const isAuthorized = useAuthorized();
    const cartItems = useSelector(selectCartItems);
    const artInCart = cartItems.find((item) => item.art.id === art.id);
    const copiesArtInCart = artInCart?.quantity;
    const defaultsAvailability = !(copiesArtInCart && Number(copiesArtInCart) === Number(art.copiesLeft));
    const { success, error } = useNotifications();
    const [artIsAvailableToBuy, setArtIsAvailableToBuy] = useState(defaultsAvailability);
    const { isOwnerTechContemporary } = useTechContemporary();
    const disableMintButtonCallback = useCallback(async () => {
        setIsMintButtonDisabled(true);
    }, []);
    const { chooseBlockchainHandler } = useChooseBlockchain({ digitalArtId: art.id, disableMintButtonCallback });
    const [isMintButtonDisabled, setIsMintButtonDisabled] = useState(false);

    useEffect(() => {
        const preventContextMenu = (e: MouseEvent) => {
            if (e.target instanceof HTMLImageElement) {
                e.preventDefault();
            }
        };
        document.addEventListener('contextmenu', preventContextMenu);
        return () => {
            document.removeEventListener('contextmenu', preventContextMenu);
        };
    }, []);

    useEffect(() => {
        const artInCart = cartItems.find((item) => item.art.id === art.id);
        const copiesArtInCart = artInCart?.quantity;
        copiesArtInCart && Number(copiesArtInCart) === Number(art.copiesLeft)
            ? setArtIsAvailableToBuy(false)
            : setArtIsAvailableToBuy(true);
    }, [cartItems]);

    const successNotification = useCallback(
        (text?: string) => {
            success({
                text: text || `${art.name} added to the cart`,
            });
        },
        [success]
    );

    const errorNotification = useCallback(
        (e?: string) => {
            error({
                text: `${e}? ${e} : ${art.name} not added to the cart`,
            });
        },
        [error]
    );

    const openCreatedCollectible = useCallback(
        (artId) => {
            push(
                generatePath(RouteE.ART_DETAILS, {
                    id: artId || 1,
                })
            );
        },
        [push]
    );

    const addToCartApi = async () => {
        try {
            const res = await CartApi.addToCart({
                digitalartlotId: art.lotId,
                quantity: 1,
            });
            if (res.data.responseMeta.status === 'OK') {
                dispatch(CartActions.addItemToCart({ quantity: 1, digitalArtLotId: art.lotId, art }));
                successNotification();
                return;
            }
            if (res.data.responseMeta.error) {
                errorNotification(res.data.responseMeta.error.messages[0]);
                setArtIsAvailableToBuy(false);
                return;
            }
        } catch (e: any) {
            errorNotification(e);
        }
    };

    const addToCart = async (e: any) => {
        e.stopPropagation();
        if (!isAuthorized) {
            AuthModal.open({
                type: 'Login',
                fullScreen: true,
            });
        } else {
            await addToCartApi();
        }
    };

    const imageHeight = useCallback((): number => {
        let height = 400;
        if (isMobileOnly) {
            height = 474;
        }
        if (isLanding || isItMyArt) {
            height = 474;
        }

        return height;
    }, [isMobileOnly, isLanding]);

    const handleDownloadArt = useCallback(async () => {
        try {
            await downloadPrivateFile('/v2/digital_art/download', art.id);
            successNotification('You have successfully downloaded the file');
        } catch (e) {
            console.error(e);
        }
    }, [art.id]);

    const getArtItem = () => (
        <StyledContainer data-test-id={testId} onClick={() => openCreatedCollectible(art.id)} isLanding={isLanding}>
            <StyledLazyLoadImage width="100%" height={imageHeight()} src={art.previewUri} alt={art.name} />
            <StyledInfo justifyContent="space-between" alignItems="center">
                <FlexBox flexDirection="column">
                    <Typography variant="title" overflowLines={1} altTitle={art.name} medium size="sm" color="gs100">
                        {art.name}
                    </Typography>
                    <Typography medium size="base" color="lightGrey" variant="title">
                        €{getEuroFromCent(art.price)}
                    </Typography>
                </FlexBox>
                {art.copiesLeft === 0 && !isItMyArt && (
                    <Typography variant="title" color="gs100" uppercase size="xs">
                        Sold Out
                    </Typography>
                )}
                {art.copiesLeft > 0 && !isOwnerTechContemporary && !isItMyArt && (
                    <StyledSvgButton onClick={addToCart} variant="text" disabled={!artIsAvailableToBuy} isIcon>
                        <SvgIcon variant="regular" icon="cart" />
                    </StyledSvgButton>
                )}
                {isItMyArt && (
                    <FlexBox>
                        <StyledSvgButton
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadArt();
                            }}
                            variant="text"
                            isIcon
                        >
                            <SvgIcon variant="regular" icon="download" />
                        </StyledSvgButton>
                        {!art.scAddress && (
                            <StyledClaimButton
                                variant="tonal"
                                size="md"
                                onClick={chooseBlockchainHandler}
                                disabled={isMintButtonDisabled}
                            >
                                <Typography size="xs" color="grey400">
                                    Claim NFT
                                </Typography>
                            </StyledClaimButton>
                        )}
                    </FlexBox>
                )}
            </StyledInfo>
            <StyledArtType>
                <div className="artType">
                    <StyledArtTypeTitle uppercase>
                        {art?.isPhysical
                            ? `${t('explore.types.physical', { ns: 'tech-contemporary' })}`
                            : `${t('explore.types.digital', { ns: 'tech-contemporary' })}`}
                    </StyledArtTypeTitle>
                </div>
            </StyledArtType>
        </StyledContainer>
    );

    return getArtItem();
};